import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LeadRecordPicker from '../../Leads/components/LeadRecordPicker';
import { leadItemChipSx } from '../../Leads/components/LeadItemChips';
import {
  appModalActionsSx,
  appModalCloseButtonSx,
  appModalHeaderDividerSx,
  appModalPaperSx,
  appModalTitleBarSx,
  appModalPrimaryButtonSx,
  appModalPrimaryLoadingSx,
  appModalSecondaryButtonSx,
  appModalTitleSx,
} from '../../Components/appModalChrome.styles';
import {
  LEADS_TEXT_SECONDARY,
  leadsAlertSx,
  leadsSectionTitleSx,
} from '../../Leads/components/leadsUi.styles';
import { getMetaErrorMessage, readMetaErrorCode } from '../metaAdsConfig';
import { META_MAX_LINK_RECORDS, describeMetaRecord, metaRecordKey } from '../metaCampaigns';
import { findMetaLinkErrorIndex } from '../hooks/useMetaLinkActions';

/**
 * «Vincular con unidades» de un anuncio o de una campaña entera. Reusa el
 * buscador de Leads (`LeadRecordPicker`, registros del espacio con
 * `ownerUserId`) con hasta 50 por vez, y muestra los ya vinculados con su label
 * (vienen en `linked_records[].label`). Espera la respuesta: si falla queda
 * abierto con el error al lado del registro (`records[i]` / `RECORD_NOT_FOUND`).
 *
 * @param {{ target: { kind: 'ad'|'campaign', name: string, linkedRecords: object[], adsCount?: number }|null,
 *   ownerUserId?: string|null, onClose: () => void,
 *   onSubmit: (records: object[]) => Promise<object> }} props
 */
export default function MetaLinkUnitsDialog({ target, ownerUserId = null, onClose, onSubmit }) {
  const open = Boolean(target);
  const [records, setRecords] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [errorIndex, setErrorIndex] = useState(null);

  useEffect(() => {
    if (!open) return;
    setRecords([]);
    setError('');
    setErrorIndex(null);
  }, [open, target]);

  const linkedKeys = new Set((target?.linkedRecords || []).map(metaRecordKey));
  const fresh = records.filter((r) => !linkedKeys.has(metaRecordKey(r)));

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (saving) return;
    if (!fresh.length) {
      setError(records.length ? 'Esas unidades ya están vinculadas.' : 'Elegí al menos una unidad.');
      return;
    }
    setSaving(true);
    setError('');
    setErrorIndex(null);
    const res = await onSubmit(fresh);
    setSaving(false);
    if (res?._httpOk || res == null) return;
    const index = findMetaLinkErrorIndex(res, fresh);
    // El índice es sobre lo mandado (`fresh`); el picker muestra `records`.
    setErrorIndex(index == null ? null : records.findIndex((r) => metaRecordKey(r) === metaRecordKey(fresh[index])));
    // Todo o nada: con un registro que falta no se vinculó ninguno.
    setError(readMetaErrorCode(res) === 'RECORD_NOT_FOUND'
      ? `${getMetaErrorMessage(res)} No se vinculó ninguna: sacala y probá de nuevo.`
      : getMetaErrorMessage(res));
  };

  const isCampaign = target?.kind === 'campaign';
  const title = isCampaign ? 'Vincular campaña con unidades' : 'Vincular anuncio con unidades';
  const linked = target?.linkedRecords || [];

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth={false}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit,
        noValidate: true,
        sx: { ...appModalPaperSx, width: 'min(520px, 95vw)', maxWidth: '520px', height: 'auto', maxHeight: 'min(90vh, 680px)' },
      }}
      aria-labelledby="meta-link-units-title"
    >
      <DialogTitle id="meta-link-units-title" component="div" sx={appModalTitleBarSx}>
        <Typography component="h2" sx={appModalTitleSx}>{title}</Typography>
        <IconButton size="small" onClick={onClose} disabled={saving} aria-label="Cerrar" sx={appModalCloseButtonSx}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>
      <Divider sx={appModalHeaderDividerSx} />
      <DialogContent sx={{ px: '20px', pt: 2, pb: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography sx={{ fontSize: '14px', fontWeight: 600, overflowWrap: 'anywhere' }}>{target?.name}</Typography>
        {isCampaign ? (
          <Typography sx={{ fontSize: '13px', color: LEADS_TEXT_SECONDARY }}>
            Se vincula cada anuncio que la campaña tiene hoy. Los anuncios que se sumen después no se vinculan solos: te los vamos a marcar como nuevos.
          </Typography>
        ) : null}
        {linked.length > 0 ? (
          <Box data-testid="meta-link-units-linked">
            <Typography sx={{ ...leadsSectionTitleSx, mb: 0.75 }}>
              {isCampaign ? 'Ya vinculadas en la campaña' : 'Ya vinculado con'}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {linked.map((rec) => (
                <Chip key={metaRecordKey(rec)} size="small" label={describeMetaRecord(rec)} sx={{ ...leadItemChipSx, minWidth: 0 }} />
              ))}
            </Box>
          </Box>
        ) : null}
        <LeadRecordPicker
          value={records}
          onChange={(next) => { setRecords(next); setError(''); setErrorIndex(null); }}
          max={META_MAX_LINK_RECORDS}
          ownerUserId={ownerUserId}
          disabled={saving}
          errorIndex={errorIndex}
          label="Sumar unidades"
          hint={`Hasta ${META_MAX_LINK_RECORDS} por vez. Un anuncio puede promocionar varias unidades.`}
          autoFocus
        />
        {error && <Alert severity="error" variant="outlined" sx={leadsAlertSx}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={appModalActionsSx}>
        <Button variant="outlined" onClick={onClose} disabled={saving} sx={appModalSecondaryButtonSx}>Cancelar</Button>
        <Button
          type="submit"
          variant="contained"
          disabled={saving}
          sx={saving ? { ...appModalPrimaryButtonSx, ...appModalPrimaryLoadingSx } : appModalPrimaryButtonSx}
          startIcon={saving ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}
        >
          Vincular
        </Button>
      </DialogActions>
    </Dialog>
  );
}
